/**
 * Utility function to automatically add a user to a group when they join a class
 * that belongs to that group
 */

import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { addMemberToStreamChannel } from "../services/streamService";
import { ChannelType } from "../config/stream";
import { notifyGroupJoined } from "./groupJoinNotification";

/**
 * Automatically adds a user to a group if they join a class that belongs to that group
 * @param {string} classId - The ID of the class being joined
 * @param {string} userId - The ID of the user joining the class
 * @param {Object} user - The user object (for context updates)
 * @param {Function} setUser - Function to update user context
 * @returns {Promise<boolean>} - Returns true if user was added to group, false otherwise
 */
export const autoJoinGroupFromClass = async (classId, userId, user, setUser) => {
  try {
    // Get the class document to check if it has a groupId
    const classRef = doc(db, "classes", classId);
    const classDoc = await getDoc(classRef);

    if (!classDoc.exists()) {
      console.warn(`[autoJoinGroupFromClass] Class ${classId} not found`);
      return false;
    }

    const classData = classDoc.data();
    const groupId = classData.groupId;

    // If the class doesn't belong to a group, nothing to do
    if (!groupId) {
      return false;
    }

    // Get the group document to check membership and group type
    const groupRef = doc(db, "groups", groupId);
    const groupDoc = await getDoc(groupRef);

    if (!groupDoc.exists()) {
      console.warn(`[autoJoinGroupFromClass] Group ${groupId} not found`);
      return false;
    }

    const groupData = groupDoc.data();
    const isPremium = groupData.isPremium === true;

    // Check if user is already a member of the group
    const memberIds = groupData.memberIds || [];
    if (memberIds.includes(userId)) {
      // User is already a member, nothing to do
      return false;
    }

    // User is not a member, add them to the group
    const userRef = doc(db, "students", userId);
    const channelType = isPremium
      ? ChannelType.PREMIUM_GROUP
      : ChannelType.STANDARD_GROUP;

    // Parallelize Firestore updates for faster execution
    await Promise.all([
      updateDoc(groupRef, { memberIds: arrayUnion(userId) }),
      updateDoc(userRef, { joinedGroups: arrayUnion(groupId) }),
    ]);

    // Update context and session storage immediately
    const updatedUser = {
      ...user,
      joinedGroups: [...(user.joinedGroups || []), groupId],
    };
    setUser(updatedUser);

    // Update session storage
    const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
    const updatedStoredUser = {
      ...storedUser,
      joinedGroups: [...(storedUser.joinedGroups || []), groupId],
    };
    sessionStorage.setItem("user", JSON.stringify(updatedStoredUser));

    // Dispatch events immediately for UI updates
    try {
      notifyGroupJoined(groupId, userId);
      window.dispatchEvent(
        new CustomEvent("userGroupsUpdated", {
          detail: { userId, groupId },
        }),
      );
    } catch (eventError) {
      console.warn(
        `[autoJoinGroupFromClass] Could not dispatch event: ${eventError.message}`,
      );
    }

    // Handle Stream operations in background (non-blocking)
    (async () => {
      try {
        await addMemberToStreamChannel({
          channelId: groupId,
          userId: userId,
          type: channelType,
          role: "channel_member",
        });

        // Refresh channels in background if premium (non-blocking)
        if (isPremium) {
          try {
            const { refreshUserChannels } = await import(
              "../services/streamConnectionService"
            );
            refreshUserChannels(userId).catch((err) => {
              console.warn(
                "[autoJoinGroupFromClass] Background channel refresh failed:",
                err,
              );
            });
          } catch (refreshError) {
            console.warn(
              "[autoJoinGroupFromClass] Error refreshing channels:",
              refreshError,
            );
          }
        }
      } catch (streamError) {
        console.error(
          "[autoJoinGroupFromClass] Stream error (non-blocking):",
          streamError,
        );
        // Non-blocking - user is already in Firestore as member
      }
    })();

    return true;
  } catch (error) {
    console.error("[autoJoinGroupFromClass] Error:", error);
    // Don't throw - class enrollment should still succeed even if group join fails
    return false;
  }
};

